import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from '../DashboardPrimitives';
import { customDashboardsAPI } from '../../../services/api';
import type { CustomDashboard } from './types';

/**
 * Rename a dashboard and edit its description.
 *
 * The title is what labels the dashboard's TAB, so this is the only way to fix a
 * title the generator chose badly — previously a dashboard was stuck with whatever
 * the planner named it, and the only remedy was deleting and regenerating.
 *
 * ⚠️ A blank title must be blocked HERE. The server's updateDashboard applies
 * `title` only when it is a non-empty string after trimming, so sending "" is
 * silently dropped: the request succeeds, the description saves, and the title
 * quietly does not change. Rejecting it client-side is what makes "Save" honest.
 */

const TITLE_MAX = 255;

const errorMessage = (e: unknown): string =>
  (e as any)?.response?.data?.message || (e as any)?.message || 'Something went wrong.';

export default function DashboardDetailsModal({
  dashboard,
  onClose,
  onSaved,
}: {
  dashboard: CustomDashboard;
  onClose: () => void;
  onSaved: (next: CustomDashboard) => void;
}) {
  const [title, setTitle] = useState(dashboard.title ?? '');
  const [description, setDescription] = useState(dashboard.description ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = title.trim();
  const canSave = trimmed.length > 0 && !busy;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setError(null);
    try {
      const res = await customDashboardsAPI.update(dashboard.id, {
        title: trimmed,
        // Trimmed-empty clears it: the server maps '' to NULL, so a description can
        // be removed as well as changed.
        description: description.trim(),
      });
      onSaved(res.data.dashboard as CustomDashboard);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal title="Dashboard details" onClose={busy ? () => {} : onClose} maxWidth="max-w-lg">
      <div className="space-y-4">
        <div>
          <label htmlFor="cd-title" className="block text-[11px] font-medium uppercase tracking-wide text-gray-400">
            Title
          </label>
          <input
            id="cd-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void save(); }}
            disabled={busy}
            autoFocus
            // Enforced in the input as well as on save, so the 255-char cap the server
            // slices to can never silently truncate what the user typed.
            maxLength={TITLE_MAX}
            className="mt-1 w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none disabled:bg-gray-50"
          />
          <div className="mt-1 flex items-center justify-between">
            <p className="text-[11px] text-gray-400">This labels the dashboard’s tab.</p>
            {title.length > TITLE_MAX - 40 && (
              <p className="text-[11px] text-gray-400">{title.length}/{TITLE_MAX}</p>
            )}
          </div>
          {trimmed.length === 0 && (
            <p className="mt-1 text-[11px] text-rose-600">A title is required.</p>
          )}
        </div>

        <div>
          <label htmlFor="cd-desc" className="block text-[11px] font-medium uppercase tracking-wide text-gray-400">
            Description
          </label>
          <textarea
            id="cd-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={busy}
            rows={3}
            placeholder="What this dashboard shows, and who it is for."
            className="mt-1 w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none disabled:bg-gray-50"
          />
          <p className="mt-1 text-[11px] text-gray-400">
            Shown under the tab strip, and as the tab’s tooltip. Leave blank to remove it.
          </p>
        </div>

        {error && <p className="text-xs text-rose-600">{error}</p>}

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={!canSave}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-primary-700 disabled:opacity-50"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}
